import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Clock, ListChecks, Sparkles } from 'lucide-react';
import { cn } from 'lib/utils';
import type { RecommendationReason, RecommendedTemplate } from '../types';

interface RecommendedTemplateRowProps {
  templates: RecommendedTemplate[];
  onSelect: (template: RecommendedTemplate) => void;
  className?: string;
}

/** "Because you chose Hiring · Sales" — the first two reasons, labelled with the onboarding option names. */
function reasonText(reasons: RecommendationReason[], t: (key: string, opts?: Record<string, unknown>) => string): string | null {
  const labels = reasons.slice(0, 2).map((reason) => {
    switch (reason.kind) {
      case 'use_case':
        return t(`auth.ob_usecase_${reason.key}`);
      case 'area':
        return t(`auth.ob_area_${reason.key}`);
      case 'grade_band':
        return t(`auth.ob_gradeband_${reason.key}`);
      default:
        return t(`auth.ob_jobrole_${reason.key}`);
    }
  });
  return labels.length ? t('templates.because_you_chose', { reasons: labels.join(' · ') }) : null;
}

/** Up to three recommended templates as compact tiles; used by onboarding, the gallery and the empty tests list. */
export function RecommendedTemplateRow({ templates, onSelect, className }: RecommendedTemplateRowProps) {
  const { t } = useTranslation();
  if (!templates.length) return null;
  return (
    <ul className={cn('grid gap-3 sm:grid-cols-3', className)}>
      {templates.map((template) => {
        const reason = reasonText(template.reasons, t);
        return (
          <li key={template.id}>
            <button
              type="button"
              onClick={() => onSelect(template)}
              className={cn(
                'flex h-full w-full flex-col rounded-lg border border-border bg-card p-4 text-left transition-colors',
                'hover:border-foreground/30 focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              {reason ? (
                <span className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                  <Sparkles className="h-3 w-3" aria-hidden />
                  {reason}
                </span>
              ) : null}
              <span className="text-sm font-semibold leading-snug text-foreground">{template.title}</span>
              <span className="mt-auto flex items-center gap-3 pt-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <ListChecks className="h-3.5 w-3.5" aria-hidden />
                  {t('templates.questions_count', { count: template.questionCount })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" aria-hidden />
                  {t('templates.minutes', { count: template.timeLimitMinutes })}
                </span>
                <ArrowRight className="ml-auto h-4 w-4 text-foreground/60" aria-hidden />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
